import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { cx } from '../lib/cx'

interface FieldChrome {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
}

interface FieldIds {
  id: string
  describedBy: string | undefined
  invalid: boolean
}

/** Habillage commun : libellé, aide, erreur, liaison ARIA (`aria-describedby`, `aria-invalid`). */
function FieldFrame({
  label,
  hint,
  error,
  required,
  className,
  id,
  children,
}: FieldChrome & { id: string; children: ReactNode }) {
  return (
    <div className={cx('field', className)}>
      {label && (
        <label className="field-label" htmlFor={id}>
          {label}
          {required && (
            <span className="req" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {hint && !error && (
        <p className="field-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field-error" id={`${id}-error`} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

function useFieldIds(idProp: string | undefined, hint: ReactNode, error: ReactNode): FieldIds {
  const generated = useId()
  const id = idProp ?? generated
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined
  return { id, describedBy, invalid: Boolean(error) }
}

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>, Omit<FieldChrome, 'className'> {
  fieldClassName?: string
  mono?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, fieldClassName, className, mono, id: idProp, ...rest },
  ref
) {
  const { id, describedBy, invalid } = useFieldIds(idProp, hint, error)
  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
      id={id}
    >
      <input
        ref={ref}
        id={id}
        className={cx('input', mono && 'mono', className)}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        {...rest}
      />
    </FieldFrame>
  )
})

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement>, Omit<FieldChrome, 'className'> {
  fieldClassName?: string
  options: SelectOption[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    required,
    fieldClassName,
    className,
    options,
    placeholder,
    id: idProp,
    ...rest
  },
  ref
) {
  const { id, describedBy, invalid } = useFieldIds(idProp, hint, error)
  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
      id={id}
    >
      <select
        ref={ref}
        id={id}
        className={cx('select', className)}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  )
})

export interface TextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement>, Omit<FieldChrome, 'className'> {
  fieldClassName?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, fieldClassName, className, id: idProp, ...rest },
  ref
) {
  const { id, describedBy, invalid } = useFieldIds(idProp, hint, error)
  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={fieldClassName}
      id={id}
    >
      <textarea
        ref={ref}
        id={id}
        className={cx('input', className)}
        required={required}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        {...rest}
      />
    </FieldFrame>
  )
})
